import React, { createContext, useContext, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

export interface CoreLayoutContextType {
  backgroundColor: string | null;
  isLogoVisible: boolean;
  isGradientVisible: boolean;
  gradientDirection: 'left' | 'right' | 'center' | 'full';
  isLogoInitialLoad: boolean;
  setBackgroundColor: (color: string | null) => void;
  showLogo: () => void;
  hideLogo: () => void;
  showGradient: (direction?: 'left' | 'right' | 'center' | 'full') => void;
  hideGradient: () => void;
  overrideGradientState: (
    visible: boolean,
    direction?: 'left' | 'right' | 'center' | 'full',
  ) => void;
  clearGradientOverride: () => void;
}

interface CoreLayoutProviderProps {
  children: React.ReactNode;
  hiddenLogoRoutes?: string[];
  gradientRoutes?: string[]; // Routes that should show gradient by default
}

const CoreLayoutContext = createContext<CoreLayoutContextType | undefined>(undefined);

// Default routes where logo should be hidden
const DEFAULT_HIDDEN_LOGO_ROUTES = ['/welcome'];

// Default routes where gradient should be visible
const DEFAULT_GRADIENT_ROUTES = ['/dashboard', '/wallet', '/manage', '/purchase', '/login'];

export const CoreLayoutProvider: React.FC<CoreLayoutProviderProps> = ({
  children,
  hiddenLogoRoutes = DEFAULT_HIDDEN_LOGO_ROUTES,
  gradientRoutes = DEFAULT_GRADIENT_ROUTES,
}) => {
  const [backgroundColor, setBackgroundColor] = useState<string | null>(null);
  const [isLogoVisible, setIsLogoVisible] = useState(true);
  const [isGradientVisible, setIsGradientVisible] = useState(false);
  const [gradientDirection, setGradientDirection] = useState<'left' | 'right' | 'center' | 'full'>(
    'full',
  );
  const [isLogoInitialLoad, setIsLogoInitialLoad] = useState(true);
  const [gradientOverride, setGradientOverride] = useState<{
    visible: boolean;
    direction?: 'left' | 'right' | 'center' | 'full';
  } | null>(null);
  const location = useLocation();

  // Check if current route should hide logo
  const shouldHideLogo = useCallback(
    (pathname: string) => {
      return hiddenLogoRoutes.some((route) => {
        return pathname === route || pathname.startsWith(route + '/');
      });
    },
    [hiddenLogoRoutes],
  );

  // Check if current route should show gradient
  const shouldShowGradient = useCallback(
    (pathname: string) => {
      return gradientRoutes.some((route) => {
        return pathname === route || pathname.startsWith(route + '/');
      });
    },
    [gradientRoutes],
  );

  // Update logo visibility based on route changes
  React.useEffect(() => {
    const currentPath = location.pathname;
    setIsLogoVisible(!shouldHideLogo(currentPath));

    // Only update gradient based on route if there's no manual override
    if (!gradientOverride) {
      setIsGradientVisible(shouldShowGradient(currentPath));

      // Set gradient direction based on specific routes
      if (currentPath === '/login') {
        setGradientDirection('left');
      } else if (shouldShowGradient(currentPath)) {
        setGradientDirection('full');
      }
    }

    // After first route change, mark logo as no longer initial load
    if (isLogoInitialLoad) {
      setTimeout(() => {
        setIsLogoInitialLoad(false);
      }, 700); // Wait for initial fade-in animation to complete
    }
  }, [location.pathname, shouldHideLogo, shouldShowGradient, isLogoInitialLoad, gradientOverride]);

  const showLogo = useCallback(() => {
    setIsLogoVisible(true);
  }, []);

  const hideLogo = useCallback(() => {
    setIsLogoVisible(false);
  }, []);

  const showGradient = useCallback((direction: 'left' | 'right' | 'center' | 'full' = 'full') => {
    setIsGradientVisible(true);
    setGradientDirection(direction);
  }, []);

  const hideGradient = useCallback(() => {
    setIsGradientVisible(false);
  }, []);

  const overrideGradientState = useCallback(
    (visible: boolean, direction: 'left' | 'right' | 'center' | 'full' = 'full') => {
      setGradientOverride({ visible, direction });
      setIsGradientVisible(visible);
      if (direction) {
        setGradientDirection(direction);
      }
    },
    [],
  );

  const clearGradientOverride = useCallback(() => {
    setGradientOverride(null);
    // Re-evaluate gradient state based on current route
    const currentPath = location.pathname;
    setIsGradientVisible(shouldShowGradient(currentPath));
    if (currentPath === '/login') {
      setGradientDirection('left');
    } else if (shouldShowGradient(currentPath)) {
      setGradientDirection('full');
    }
  }, [location.pathname, shouldShowGradient]);

  const value: CoreLayoutContextType = {
    backgroundColor,
    isLogoVisible,
    isGradientVisible,
    gradientDirection,
    isLogoInitialLoad,
    setBackgroundColor,
    showLogo,
    hideLogo,
    showGradient,
    hideGradient,
    overrideGradientState,
    clearGradientOverride,
  };

  return <CoreLayoutContext.Provider value={value}>{children}</CoreLayoutContext.Provider>;
};

export const useCoreLayout = (): CoreLayoutContextType => {
  const context = useContext(CoreLayoutContext);
  if (context === undefined) {
    throw new Error('useCoreLayout must be used within a CoreLayoutProvider');
  }
  return context;
};

export default CoreLayoutContext;
